import { PrivateURLConstants } from '@constants/private-url.constant';
import { Integration } from '@testsigmainc/constants';
import { IosRealDevice } from '@models/ios-real-device.model';
import { NOOP } from '@testsigma/atoms';
import { Page, Upload, UploadVersion } from '@testsigmainc/models';
import axios from 'axios';

import { useQuery } from 'react-query';
import api from '../api/instance';

const nameSpace = PrivateURLConstants.UPLOADS;
const nameSpaceUploadVersion = PrivateURLConstants.UPLOAD_VERSIONS;

type findAllProps = {
  query: string;
  sort?: string;
  size?: number;
  page?: number;
};

type useUploadsFindAllProps = {
  payload: findAllProps;
  enabled: boolean;
  callback: (status, response) => void;
  beforeQuery?: () => void;
  key?;
};

const findAll = (
  query?: string,
  sort?: string,
  size?: number,
  page?: number,
  isList?: boolean,
): Page<Upload> => {
  return api
    .get(`${nameSpace}${isList ? '/list' : ''}`, { query, sort, size, page })
    .then((res) => new Page<Upload>().deserialize(res, Upload))
    .catch((e) => Promise.reject(e));
};

const bulkDeleted = (ids: number[]) => {
  return api
    .remove(`${nameSpace}/bulk?${`ids[]`}=${ids.join()}`)
    .then((res) => res)
    .catch((e) => Promise.reject(e));
};

const destroy = (id: number) => {
  return api
    .remove(`${nameSpace}/${id}`)
    .then((res) => res)
    .catch((e) => Promise.reject(e));
};

const show = (id: number) => {
  return api
    .get(nameSpace + '/' + id)
    .then((res) => new Upload().deserialize(res))
    .catch((e) => Promise.reject(e));
};

const create = (formData: FormData) => {
  return api
    .post(nameSpace, formData, 'multipart/form-data')
    .then((res) => new Upload().deserialize(res))
    .catch((e) => Promise.reject(e));
};

const createWithCancel = async (formData: FormData, controller: any) => {
  return await axios
    .post(nameSpace, formData, { signal: controller.signal })
    .then((res) => new Upload().deserialize(res.data))
    .catch((e) => Promise.reject(e));
};

const update = (id: number, formData: FormData) => {
  return api
    .put(`${nameSpace}/${id}`, formData, null, 'multipart/form-data')
    .then((res) => new Upload().deserialize(res))
    .catch((e) => Promise.reject(e));
};

const updateWithCancel = async (
  id: number,
  formData: FormData,
  controller: any,
) => {
  return await axios
    .put(`${nameSpace}/${id}`, formData, { signal: controller.signal })
    .then((res) => new Upload().deserialize(res.data))
    .catch((e) => Promise.reject(e));
};

const createIosRealDevice = (deviceDetails: IosRealDevice) => {
  return api
    .put(nameSpaceUploadVersion, deviceDetails)
    .then((res) => {
      return res;
    })
    .catch((e) => {
      throw e;
    });
};

const uploadToPlugin = (
  uploadVersionId: number,
  plugin: Integration,
): Promise<UploadVersion> => {
  return api
    .put(
      `${nameSpaceUploadVersion}/upload_to_plugin/${uploadVersionId}/${plugin}`,
      null,
    )
    .then((res) => new UploadVersion().deserialize(res));
};

export const useUploadsFindAll = ({
  payload,
  key = null,
  callback,
  beforeQuery = NOOP,
}: useUploadsFindAllProps) => {
  return useQuery({
    queryKey: key || payload,
    queryFn: () => {
      beforeQuery();
      return findAll(
        payload.query,
        payload.sort,
        payload.size,
        payload.page,
        true,
      );
    },
    onSuccess: (data) => callback(true, data),
    onError: (err) => callback(false, err),
  });
};

const UploadService = {
  findAll,
  bulkDeleted,
  destroy,
  create,
  update,
  createIosRealDevice,
  createWithCancel,
  updateWithCancel,
  show,
  uploadToPlugin,
};

export default UploadService;
